import { format } from 'date-fns';
import { type FC } from 'react';
import styled from 'styled-components';

import { OrderType } from '@/api/order/models/OrderType';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { FundOrder } from '@/pages/Order/types/Order';

interface Props {
  orderType: FundOrder['orderType'];
  isPastCutoff: boolean;
  cutOffTime: Date;
}

export const CutoffInformationAlert: FC<Props> = ({
  orderType,
  isPastCutoff,
  cutOffTime,
}) => {
  const { orders } = useTexts();

  const pastCutoffTitle = {
    [OrderType.Subscription]:
      orders.form.information.subscription.afterCutoff.title,
    [OrderType.Redemption]:
      orders.form.information.redemption.afterCutoff.title,
    [OrderType.Switch]: orders.form.information.switch.afterCutoff.title,
  };

  const pastCutoffMessage = {
    [OrderType.Subscription]:
      orders.form.information.subscription.afterCutoff.message,
    [OrderType.Redemption]:
      orders.form.information.redemption.afterCutoff.message,
    [OrderType.Switch]: orders.form.information.switch.afterCutoff.message,
  };

  const beforeCutoffTitle = {
    [OrderType.Subscription]:
      orders.form.information.subscription.beforeCutoff.title,
    [OrderType.Redemption]:
      orders.form.information.redemption.beforeCutoff.title,
    [OrderType.Switch]: orders.form.information.switch.beforeCutoff.title,
  };

  const beforeCutoffMessage = {
    [OrderType.Subscription]:
      orders.form.information.subscription.beforeCutoff.message,
    [OrderType.Redemption]:
      orders.form.information.redemption.beforeCutoff.message,
    [OrderType.Switch]: orders.form.information.switch.beforeCutoff.message,
  };

  const title = isPastCutoff
    ? pastCutoffTitle[orderType]
    : beforeCutoffTitle[orderType];

  const message = isPastCutoff
    ? pastCutoffMessage[orderType]
    : beforeCutoffMessage[orderType];

  const titleWithTime = fmsWithTemplate(title, {
    time: format(cutOffTime, 'HH.mm (O)'), // E.g. "15.00 (GMT+2)"
  });

  const messageWithTime = fmsWithTemplate(message, {
    time: format(cutOffTime, 'HH.mm (O)'), // E.g. "15.00 (GMT+2)"
  });

  return (
    <ConnectSurface data-test="cutoff-information-alert">
      <WithGap direction="row">
        <Container>
          {isPastCutoff ? (
            <img
              alt="Money padlock"
              title="Money padlock"
              src={`${window.config.storeblocksAssetsBaseUrl}/icons/money-padlock.svg`}
              height="96px"
              width="96px"
            />
          ) : (
            <img
              alt="Clock coins"
              title="Clock coins"
              src={`${window.config.storeblocksAssetsBaseUrl}/icons/clock-coins.svg`}
              height="96px"
              width="96px"
            />
          )}
        </Container>

        <Container>
          <WithGap gap="8">
            <p className="font-body-large-demibold">{titleWithTime}</p>
            <p className="font-body-small">{messageWithTime}</p>
          </WithGap>
        </Container>
      </WithGap>
    </ConnectSurface>
  );
};

const Container = styled.div`
  display: flex;
  align-items: center;
`;
